import React, { FC, useState, useMemo } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Tooltip,
    IconButton,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Chip,
    Stack,
    Tabs,
    Tab,
    FormControlLabel,
    Checkbox,
    Alert,
    InputAdornment,
    MenuItem
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import NumbersIcon from '@mui/icons-material/Numbers';

import { BIRTH_NUMBERS, getBirthNumberTitle, getBirthNumberItem } from '../../../utils/birthNumbers';

export const NumberBadge: FC<{
    num: number | string;
    size?: number;
    showTitle?: boolean;
    fontSize?: number | string;
    shape?: 'squircle' | 'circle';
}> = ({ num, size = 28, showTitle = true, fontSize = '0.85rem', shape = 'circle' }) => {
    const item = getBirthNumberItem(num);
    const n = typeof num === 'string' ? parseInt(num, 10) : num;
    const bgGradient = item?.bgGradient || 'linear-gradient(135deg, #42a5f5 0%, #1565c0 100%)';
    const borderRadius = shape === 'circle' ? '50%' : '6px';

    return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.8, verticalAlign: 'middle' }}>
            <Box
                sx={{
                    width: size,
                    height: size,
                    borderRadius,
                    background: bgGradient,
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
                    userSelect: 'none',
                    flexShrink: 0,
                    fontWeight: 800,
                    fontSize: size <= 26 ? '0.72rem' : '0.85rem'
                }}
            >
                {n}
            </Box>
            {showTitle && (
                <Typography component="span" sx={{ fontWeight: 600, fontSize, color: '#263238' }}>
                    {getBirthNumberTitle(n)}
                </Typography>
            )}
        </Box>
    );
};

interface NumberMatrixTableProps {
    numberPairs: Record<string, string>;
    onSavePair: (userNum: string, partnerNum: string, text: string, applyBothWays: boolean) => void;
    onDeletePair: (key: string) => void;
}

export const NumberMatrixTable: FC<NumberMatrixTableProps> = ({
    numberPairs,
    onSavePair,
    onDeletePair
}) => {
    const [viewMode, setViewMode] = useState<'matrix' | 'list'>('matrix');
    const [searchQuery, setSearchQuery] = useState<string>('');

    // Состояние модального окна редактирования
    const [dialogOpen, setDialogOpen] = useState<boolean>(false);
    const [dialogUserNum, setDialogUserNum] = useState<string>('7');
    const [dialogPartnerNum, setDialogPartnerNum] = useState<string>('15');
    const [dialogText, setDialogText] = useState<string>('');
    const [applyBothWays, setApplyBothWays] = useState<boolean>(true);

    const [savedNotification, setSavedNotification] = useState<string | null>(null);

    const configuredCount = Object.keys(numberPairs).length;

    const handleOpenEdit = (userNum: string | number, partnerNum: string | number) => {
        const u = String(userNum);
        const p = String(partnerNum);
        const key = `${u}_${p}`;
        setDialogUserNum(u);
        setDialogPartnerNum(p);
        setDialogText(numberPairs[key] || '');
        setApplyBothWays(true);
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
    };

    const handleSaveFromDialog = () => {
        onSavePair(dialogUserNum, dialogPartnerNum, dialogText, applyBothWays);
        const msg = dialogText.trim()
            ? `Сохранено: ответ для пары чисел «${dialogUserNum} + ${dialogPartnerNum}»`
            : `Сохранено: ответ для пары чисел «${dialogUserNum} + ${dialogPartnerNum}» сброшен`;
        setSavedNotification(msg);
        setDialogOpen(false);
        setTimeout(() => {
            setSavedNotification(null);
        }, 5000);
    };

    const handleDeleteFromDialog = () => {
        const key = `${dialogUserNum}_${dialogPartnerNum}`;
        onDeletePair(key);
        if (applyBothWays) {
            const reverseKey = `${dialogPartnerNum}_${dialogUserNum}`;
            if (numberPairs[reverseKey]) {
                onDeletePair(reverseKey);
            }
        }
        setSavedNotification(`Сохранено: ответ для пары чисел «${dialogUserNum} + ${dialogPartnerNum}» удален`);
        setDialogOpen(false);
        setTimeout(() => {
            setSavedNotification(null);
        }, 5000);
    };

    // Фильтрация списка настроенных пар
    const filteredList = useMemo(() => {
        return Object.entries(numberPairs).filter(([key, text]) => {
            if (searchQuery.trim()) {
                const [u, p] = key.split('_');
                const q = searchQuery.toLowerCase();
                const matchNumbers = u.includes(q) || p.includes(q);
                const matchText = text.toLowerCase().includes(q);
                return matchNumbers || matchText;
            }
            return true;
        });
    }, [numberPairs, searchQuery]);

    const activePairKey = `${dialogUserNum}_${dialogPartnerNum}`;
    const hasActiveText = Boolean(numberPairs[activePairKey]);

    return (
        <Box sx={{ mt: 2 }}>
            {/* Панель управления и переключатель видов */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Tabs
                        value={viewMode}
                        onChange={(e, val) => setViewMode(val)}
                        sx={{
                            minHeight: 36,
                            '& .MuiTab-root': {
                                minHeight: 36,
                                py: 0.5,
                                px: 2,
                                fontSize: '0.85rem',
                                fontWeight: 700,
                                textTransform: 'none'
                            }
                        }}
                    >
                        <Tab value="matrix" label="Таблица чисел (1–31)" />
                        <Tab
                            value="list"
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                                    <span>Настроенные ответы</span>
                                    <Chip
                                        size="small"
                                        label={configuredCount}
                                        color={configuredCount > 0 ? 'success' : 'default'}
                                        sx={{ height: 20, fontSize: '0.75rem', fontWeight: 700 }}
                                    />
                                </Box>
                            }
                        />
                    </Tabs>
                </Box>
            </Box>

            {savedNotification && (
                <Alert
                    severity="success"
                    onClose={() => setSavedNotification(null)}
                    sx={{
                        mb: 2,
                        borderRadius: 2,
                        boxShadow: '0 2px 8px rgba(46,125,50,0.15)',
                        border: '1px solid #81c784',
                        bgcolor: 'rgba(232, 245, 233, 0.9)',
                        alignItems: 'center',
                        fontWeight: 600
                    }}
                >
                    {savedNotification}
                </Alert>
            )}

            {/* ВИД 1: МАТРИЦА ЧИСЕЛ РОЖДЕНИЯ 31x31 */}
            {viewMode === 'matrix' && (
                <Paper
                    variant="outlined"
                    sx={{
                        borderRadius: 2.5,
                        overflow: 'hidden',
                        mb: 2,
                        bgcolor: 'transparent',
                        background: 'transparent',
                        border: '1px solid rgba(0, 0, 0, 0.12)'
                    }}
                >
                    <Box
                        sx={{
                            p: 1.5,
                            bgcolor: 'transparent',
                            borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 1
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <NumbersIcon sx={{ color: '#00897b', fontSize: 20 }} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#00695c' }}>
                                Матрица чисел дня рождения (1–31): строки — день пользователя, столбцы — день партнера
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, fontSize: '0.75rem', color: '#546e7a' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Box sx={{ width: 14, height: 14, borderRadius: '3px', bgcolor: 'rgba(0, 137, 123, 0.2)', border: '1px solid #26a69a' }} />
                                <span>Настроен ответ</span>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Box sx={{ width: 14, height: 14, borderRadius: '3px', bgcolor: 'transparent', border: '1px solid #cfd8dc' }} />
                                <span>По умолчанию</span>
                            </Box>
                        </Box>
                    </Box>

                    <TableContainer sx={{ overflowX: 'auto', bgcolor: 'transparent', width: '100%' }}>
                        <Table
                            size="small"
                            stickyHeader
                            sx={{
                                width: '100%',
                                tableLayout: 'fixed',
                                borderCollapse: 'separate',
                                borderSpacing: 0
                            }}
                        >
                            <TableHead>
                                <TableRow>
                                    <TableCell
                                        sx={{
                                            bgcolor: 'transparent',
                                            fontWeight: 800,
                                            fontSize: '0.72rem',
                                            color: '#546e7a',
                                            borderBottom: '2px solid rgba(0, 0, 0, 0.12)',
                                            borderRight: '2px solid rgba(0, 0, 0, 0.12)',
                                            position: 'sticky',
                                            left: 0,
                                            zIndex: 4,
                                            width: '52px',
                                            minWidth: '52px',
                                            textAlign: 'center',
                                            p: 0.3
                                        }}
                                    />
                                    {BIRTH_NUMBERS.map(p => (
                                        <TableCell
                                            key={p.number}
                                            align="center"
                                            sx={{
                                                bgcolor: 'transparent',
                                                p: 0.3,
                                                borderBottom: '2px solid rgba(0, 0, 0, 0.12)',
                                                borderRight: '1px solid rgba(0, 0, 0, 0.08)',
                                                zIndex: 2,
                                                width: '32px',
                                                minWidth: '30px'
                                            }}
                                        >
                                            <Tooltip title={`${p.number}-е число`} arrow placement="top">
                                                <Typography
                                                    sx={{
                                                        fontSize: '0.75rem',
                                                        fontWeight: 800,
                                                        color: p.color,
                                                        cursor: 'default'
                                                    }}
                                                >
                                                    {p.number}
                                                </Typography>
                                            </Tooltip>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {BIRTH_NUMBERS.map(u => (
                                    <TableRow key={u.number} hover sx={{ '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' } }}>
                                        <TableCell
                                            component="th"
                                            scope="row"
                                            align="center"
                                            sx={{
                                                bgcolor: 'transparent',
                                                p: 0.3,
                                                borderRight: '2px solid rgba(0, 0, 0, 0.12)',
                                                borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                                                position: 'sticky',
                                                left: 0,
                                                zIndex: 3
                                            }}
                                        >
                                            <Tooltip title={`${u.number}-е число`} arrow placement="right">
                                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, color: u.color }}>
                                                    {u.number}
                                                </Typography>
                                            </Tooltip>
                                        </TableCell>

                                        {BIRTH_NUMBERS.map(p => {
                                            const pairKey = `${u.number}_${p.number}`;
                                            const customText = numberPairs[pairKey];
                                            const isConfigured = Boolean(customText);

                                            return (
                                                <TableCell
                                                    key={p.number}
                                                    align="center"
                                                    onClick={() => handleOpenEdit(u.number, p.number)}
                                                    sx={{
                                                        p: 0.1,
                                                        borderRight: '1px solid rgba(0, 0, 0, 0.05)',
                                                        borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.12s ease',
                                                        bgcolor: isConfigured ? 'rgba(0, 137, 123, 0.18)' : 'transparent',
                                                        '&:hover': {
                                                            bgcolor: isConfigured ? 'rgba(0, 137, 123, 0.32)' : 'rgba(0, 137, 123, 0.1)',
                                                            transform: 'scale(1.08)',
                                                            zIndex: 1,
                                                            boxShadow: '0 2px 6px rgba(0,0,0,0.1)'
                                                        }
                                                    }}
                                                >
                                                    <Tooltip
                                                        title={
                                                            <Box sx={{ p: 0.5, maxWidth: 260 }}>
                                                                <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', mb: 0.5, color: '#80cbc4' }}>
                                                                    {u.number}-е число + {p.number}-е число
                                                                </Typography>
                                                                {isConfigured ? (
                                                                    <Typography sx={{ fontSize: '0.75rem', lineHeight: 1.35 }}>
                                                                        «{customText}»
                                                                    </Typography>
                                                                ) : (
                                                                    <Typography sx={{ fontSize: '0.72rem', color: '#b0bec5', fontStyle: 'italic' }}>
                                                                        Ответ по умолчанию (кликните для настройки)
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        }
                                                        arrow
                                                    >
                                                        <Box
                                                            sx={{
                                                                height: 28,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center'
                                                            }}
                                                        >
                                                            {isConfigured ? (
                                                                <Box
                                                                    sx={{
                                                                        width: 18,
                                                                        height: 18,
                                                                        borderRadius: '50%',
                                                                        bgcolor: '#00897b',
                                                                        color: '#fff',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        boxShadow: '0 1px 3px rgba(0,137,123,0.4)'
                                                                    }}
                                                                >
                                                                    <ChatBubbleOutlineIcon sx={{ fontSize: 11 }} />
                                                                </Box>
                                                            ) : (
                                                                <Typography sx={{ fontSize: '0.65rem', color: '#cfd8dc' }}>
                                                                    ·
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </Tooltip>
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}

            {/* ВИД 2: СПИСОК НАСТРОЕННЫХ ПАР ЧИСЕЛ */}
            {viewMode === 'list' && (
                <Box>
                    <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                        <TextField
                            size="small"
                            placeholder="Поиск по числам (например, 15) или тексту ответа..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                    </InputAdornment>
                                ),
                                endAdornment: searchQuery && (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => setSearchQuery('')}>
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                            sx={{ maxWidth: 400, flex: 1 }}
                        />
                        <Button
                            variant="contained"
                            color="success"
                            startIcon={<EditIcon />}
                            onClick={() => handleOpenEdit(7, 15)}
                            sx={{ fontWeight: 600, textTransform: 'none' }}
                        >
                            Добавить пару
                        </Button>
                    </Box>

                    {filteredList.length === 0 ? (
                        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2, bgcolor: 'transparent', border: '1px dashed #cfd8dc' }} elevation={0}>
                            <Typography sx={{ color: 'text.secondary', mb: 1.5 }}>
                                {configuredCount === 0
                                    ? 'Пока не настроено ни одного персонального ответа по числам рождения.'
                                    : 'По вашему запросу ничего не найдено.'}
                            </Typography>
                            {configuredCount === 0 && (
                                <Button
                                    variant="outlined"
                                    color="success"
                                    onClick={() => handleOpenEdit(7, 15)}
                                    sx={{ textTransform: 'none', fontWeight: 600 }}
                                >
                                    Настроить первую пару (например, 7-е число + 15-е число)
                                </Button>
                            )}
                        </Paper>
                    ) : (
                        <Stack spacing={1.5}>
                            {filteredList.map(([pairKey, text]) => {
                                const [uNum, pNum] = pairKey.split('_');
                                return (
                                    <Paper
                                        key={pairKey}
                                        elevation={0}
                                        sx={{
                                            p: 2,
                                            borderRadius: 2,
                                            border: '1px solid #e0e0e0',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 2,
                                            transition: 'border-color 0.2s',
                                            '&:hover': { borderColor: '#00897b', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 260 }}>
                                            <NumberBadge num={uNum} size={28} />
                                            <SwapHorizIcon sx={{ color: '#9e9e9e' }} />
                                            <NumberBadge num={pNum} size={28} />
                                        </Box>

                                        <Box sx={{ flex: 1, px: 1 }}>
                                            <Typography variant="body2" sx={{ color: '#37474f', fontStyle: 'italic' }}>
                                                «{text}»
                                            </Typography>
                                        </Box>

                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Tooltip title="Редактировать ответ">
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={() => handleOpenEdit(uNum, pNum)}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Удалить ответ">
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => {
                                                        onDeletePair(pairKey);
                                                        setSavedNotification(`Сохранено: ответ для пары чисел удален`);
                                                    }}
                                                >
                                                    <DeleteOutlineIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Paper>
                                );
                            })}
                        </Stack>
                    )}
                </Box>
            )}

            {/* ДИАЛОГ РЕДАКТИРОВАНИЯ ПАРЫ ЧИСЕЛ */}
            <Dialog
                open={dialogOpen}
                onClose={handleCloseDialog}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 2.5, p: 1 }
                }}
            >
                <DialogTitle sx={{ pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#00695c' }}>
                        Настройка ответа по числам рождения
                    </Typography>
                    <IconButton size="small" onClick={handleCloseDialog}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers sx={{ pt: 2 }}>
                    <Box sx={{ mb: 2.5 }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                            Выберите день рождения пользователя и партнера (1–31) и задайте специальный ответ:
                        </Typography>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" sx={{ mb: 2.5 }}>
                            <TextField
                                select
                                fullWidth
                                label="День пользователя"
                                value={dialogUserNum}
                                onChange={(e) => {
                                    setDialogUserNum(e.target.value);
                                    const nextKey = `${e.target.value}_${dialogPartnerNum}`;
                                    setDialogText(numberPairs[nextKey] || '');
                                }}
                                size="small"
                            >
                                {BIRTH_NUMBERS.map(s => (
                                    <MenuItem key={s.number} value={String(s.number)}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <NumberBadge num={s.number} size={22} showTitle={false} />
                                            <span>{s.title} ({s.number})</span>
                                        </Box>
                                    </MenuItem>
                                ))}
                            </TextField>

                            <IconButton
                                onClick={() => {
                                    const tmp = dialogUserNum;
                                    setDialogUserNum(dialogPartnerNum);
                                    setDialogPartnerNum(tmp);
                                    const nextKey = `${dialogPartnerNum}_${tmp}`;
                                    setDialogText(numberPairs[nextKey] || '');
                                }}
                                sx={{ bgcolor: 'rgba(0,0,0,0.04)', p: 1 }}
                                title="Поменять местами"
                            >
                                <SwapHorizIcon />
                            </IconButton>

                            <TextField
                                select
                                fullWidth
                                label="День партнера"
                                value={dialogPartnerNum}
                                onChange={(e) => {
                                    setDialogPartnerNum(e.target.value);
                                    const nextKey = `${dialogUserNum}_${e.target.value}`;
                                    setDialogText(numberPairs[nextKey] || '');
                                }}
                                size="small"
                            >
                                {BIRTH_NUMBERS.map(s => (
                                    <MenuItem key={s.number} value={String(s.number)}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <NumberBadge num={s.number} size={22} showTitle={false} />
                                            <span>{s.title} ({s.number})</span>
                                        </Box>
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Stack>

                        <Box
                            sx={{
                                p: 1.5,
                                borderRadius: 2,
                                bgcolor: 'rgba(0, 137, 123, 0.06)',
                                border: '1px solid rgba(0, 137, 123, 0.2)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 2,
                                mb: 2.5
                            }}
                        >
                            <NumberBadge num={dialogUserNum} size={32} />
                            <Typography sx={{ fontWeight: 800, color: '#00897b', fontSize: '1.2rem' }}>+</Typography>
                            <NumberBadge num={dialogPartnerNum} size={32} />
                        </Box>

                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label="Текст персонального ответа для пары чисел"
                            placeholder="Например: Людям, рожденным 7-го и 15-го числа, суждено притягивать друг друга глубокой интуицией..."
                            value={dialogText}
                            onChange={(e) => setDialogText(e.target.value)}
                            helperText="Оставьте пустым или нажмите «Удалить ответ», чтобы сбросить на стандартный ответ."
                        />

                        {dialogUserNum !== dialogPartnerNum && (
                            <Box sx={{ mt: 1.5 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={applyBothWays}
                                            onChange={(e) => setApplyBothWays(e.target.checked)}
                                            color="success"
                                        />
                                    }
                                    label={
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                            Применить симметрично в обе стороны ({dialogUserNum}-е + {dialogPartnerNum}-е и {dialogPartnerNum}-е + {dialogUserNum}-е)
                                        </Typography>
                                    }
                                />
                            </Box>
                        )}
                    </Box>
                </DialogContent>

                <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
                    {hasActiveText ? (
                        <Button
                            variant="outlined"
                            color="error"
                            onClick={handleDeleteFromDialog}
                            startIcon={<DeleteOutlineIcon />}
                        >
                            Удалить ответ
                        </Button>
                    ) : <Box />}

                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button onClick={handleCloseDialog} color="inherit">
                            Отмена
                        </Button>
                        <Button
                            variant="contained"
                            color="success"
                            onClick={handleSaveFromDialog}
                            startIcon={<CheckCircleIcon />}
                            sx={{ fontWeight: 600 }}
                        >
                            Сохранить
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default NumberMatrixTable;
